import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { PolicyGate } from "@/components/policies";
import { FullPageSpinner } from "@/components/ui";
import { session } from "@/lib/api";
import { config } from "@/lib/config";
import type { SessionState } from "@/lib/session";

export function useSession(): SessionState {
  return useSyncExternalStore(session.subscribe, session.getSnapshot);
}

/**
 * The signed-in user (only valid under <RequireAuth>). It also answers while the session has ended
 * and the re-login prompt is up (`expired`): the page stays mounted under it (PHP-3322).
 */
export function useUser() {
  const s = useSession();
  if (s.status !== "signed_in" && s.status !== "expired") throw new Error("useUser() outside <RequireAuth>");
  return s.user;
}

/**
 * Renders children for signed-in users who have accepted every mandatory policy; sends everyone else to
 * /login and back afterwards. A session that has *ended* (`expired`) keeps the page: <SessionGuard/> asks
 * for the password over it, so nothing the user typed is lost. Only an explicit sign-out (`signed_out`)
 * leaves.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.status === "loading") return <FullPageSpinner />;
  if (s.status === "signed_out") {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <PolicyGate>{children}</PolicyGate>;
}

/** Auth pages (login, register…) bounce signed-in users to where they were going. */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.status === "loading") return <FullPageSpinner />;
  if (s.status === "signed_in") {
    const next = new URLSearchParams(location.search).get("next");
    const builder = builderNext(next);
    if (builder) return <LeaveFor url={builder} />;
    return <Navigate to={safeNext(next)} replace />;
  }
  return <>{children}</>;
}

/** Only same-app relative paths are allowed as post-login destinations (no open redirects). */
export function safeNext(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}

/**
 * A post-login destination in the AI website builder, a separate app: an absolute http(s) URL
 * on the configured builder origin, else null. No other origin is ever allowed (no open redirects).
 */
export function builderNext(next: string | null, builderUrl: string = config.builderUrl): string | null {
  if (!next) return null;
  let url: URL;
  try {
    url = new URL(next);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.origin === new URL(builderUrl).origin ? url.href : null;
}

/** The `next` worth keeping across a detour (federated sign-in, register): the builder, or a page here. */
export function keptNext(next: string | null): string | null {
  return builderNext(next) ?? (next ? safeNext(next) : null);
}

/** Leaves the portal for another CortexGrip app. An object, so tests can replace it. */
export const away = {
  go(url: string): void {
    window.location.replace(url);
  },
};

function LeaveFor({ url }: { url: string }) {
  useEffect(() => away.go(url), [url]);
  return <FullPageSpinner />;
}
